import React, { useRef, useState } from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography, CircularProgress, Alert } from '@mui/material';
import { PhotoCamera as CameraIcon, Close as RemoveIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import HorsePhotoAvatar from './HorsePhotoAvatar';
import {
  removeHorsePhoto,
  uploadHorsePhoto,
  fetchHorsePhotoUrl,
  horsePhotoQueryKey,
} from '../utils/horsePhotos';
import { skipWhenOffline, useIsOffline } from '../offline';

export interface PhotoUploaderProps {
  /** Horse id. The photo is uploaded against this horse. */
  horseId: string | undefined | null;
  /** Optional name to render under the avatar as a caption. */
  caption?: string;
  /** Pixel size of the avatar. Defaults to 96. */
  size?: number;
}

/**
 * Square avatar uploader for the Add/Edit Patient dialog. Click anywhere on
 * the tile to open the file picker; a tiny camera icon in the corner advertises
 * the affordance. When a photo is set, a remove (×) button appears.
 *
 * Photos are downscaled client-side and uploaded to S3 via the Attachments
 * API (`horsePhotos.ts`). On success we invalidate the horse-photo / horse /
 * horses queries so every avatar (this tile, the list rows, the chart banner)
 * refreshes.
 */
const PhotoUploader: React.FC<PhotoUploaderProps> = ({ horseId, caption, size = 96 }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Photo bytes are never queued offline — the upload is a multipart S3 round
  // trip, so the whole surface is disabled while disconnected.
  const isOffline = useIsOffline();

  const refreshAvatars = () => {
    queryClient.invalidateQueries(horsePhotoQueryKey(horseId));
    if (horseId) queryClient.invalidateQueries(['horse', horseId]);
    queryClient.invalidateQueries('horses');
  };

  const handleFile = async (file: File) => {
    if (!horseId) {
      setError('Save the patient first, then add a photo.');
      return;
    }
    // Covers the race where connectivity drops after the picker opened.
    if (!skipWhenOffline()) {
      setError('Adding photos needs a connection');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await uploadHorsePhoto(horseId, file);
      refreshAvatars();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Could not save the photo');
    } finally {
      setBusy(false);
    }
  };

  const onPickClick = () => inputRef.current?.click();

  const onInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await handleFile(file);
    // Reset so picking the same file again still triggers change.
    if (inputRef.current) inputRef.current.value = '';
  };

  const onRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!horseId) return;
    if (!skipWhenOffline()) {
      setError('Removing the photo needs a connection');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await removeHorsePhoto(horseId);
      refreshAvatars();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Could not remove the photo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <Tooltip title={isOffline ? 'Adding photos needs a connection' : ''} arrow>
          {/* span so the tooltip still fires over the disabled button */}
          <span style={{ display: 'inline-flex' }}>
        <ButtonBase
          onClick={onPickClick}
          disabled={busy || !horseId || isOffline}
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            position: 'relative',
            overflow: 'visible',
            transition: 'transform 120ms',
            '&:hover': { transform: 'scale(1.02)' },
            '&:hover .upload-hover-veil': { opacity: 1 },
          }}
          data-testid="photo-uploader-trigger"
        >
          {/* No photoUrl prop -> self-fetches by horseId (shared cache with the
              remove button below). Refetches after upload via invalidation. */}
          <HorsePhotoAvatar horseId={horseId} size={size} />
          {/* Hover overlay invites the click */}
          <Box
            className="upload-hover-veil"
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 120ms',
              pointerEvents: 'none',
            }}
          >
            <CameraIcon sx={{ color: '#fff', fontSize: size * 0.32 }} />
          </Box>
          {/* Small camera badge in the bottom-right for first-time discovery */}
          <Box
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: '#C9772B',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
              border: '2px solid #fff',
            }}
          >
            {busy ? (
              <CircularProgress size={14} sx={{ color: '#fff' }} />
            ) : (
              <CameraIcon sx={{ fontSize: 14 }} />
            )}
          </Box>
        </ButtonBase>
          </span>
        </Tooltip>

        {/* Remove button: visible only when a photo is set */}
        {horseId && (
          <RemoveButton horseId={horseId} onRemove={onRemove} busy={busy} />
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onInputChange}
          data-testid="photo-uploader-input"
        />
      </Box>

      {caption && (
        <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: size + 40 }}>
          {caption}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ py: 0, px: 1, fontSize: '0.7rem' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!horseId && (
        <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', fontSize: '0.68rem' }}>
          Save the patient first, then add a photo
        </Typography>
      )}
    </Box>
  );
};

/**
 * Sub-component that renders the × button only when a photo actually exists.
 * It reads the same react-query key as the avatar above, so the lookup is
 * deduplicated/shared rather than fetched twice.
 */
const RemoveButton: React.FC<{
  horseId: string;
  onRemove: (e: React.MouseEvent) => void;
  busy: boolean;
}> = ({ horseId, onRemove, busy }) => {
  // Photo removal is a server delete — disabled offline (never queued).
  const isOffline = useIsOffline();
  const { data: photoUrl } = useQuery(
    horsePhotoQueryKey(horseId),
    () => fetchHorsePhotoUrl(horseId),
    { enabled: !!horseId, staleTime: 5 * 60_000 },
  );
  if (!photoUrl) return null;
  return (
    <Tooltip title={isOffline ? 'Removing the photo needs a connection' : 'Remove photo'} arrow>
      {/* the span (not the disable-able button) anchors the tooltip; it also
          carries the absolute positioning so hover still has a hit target */}
      <span style={{ position: 'absolute', right: -8, top: -8, zIndex: 1 }}>
        <IconButton
          size="small"
          onClick={onRemove}
          disabled={busy || isOffline}
          sx={{
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: 'divider',
            width: 22,
            height: 22,
            '&:hover': { bgcolor: '#FFEBEE' },
          }}
          data-testid="photo-uploader-remove"
          aria-label="Remove photo"
        >
          <RemoveIcon sx={{ fontSize: 14, color: 'error.main' }} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default PhotoUploader;
